import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Wand2,
  Loader2,
  ImageIcon,
  Download,
  RefreshCw,
  LayoutGrid,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  X,
  Upload,
  Cpu,
  Maximize2,
  Gauge,
  Coins,
  SlidersHorizontal,
  Pencil,
} from 'lucide-react';
import { api } from '../../services/api';
import {
  ecommerceReplicateAPI,
  type EcommerceReplicateTask,
  type EcommerceReplicateNode,
  type EcommerceReplicateTaskDetail,
  type EcommerceCreditsPreview,
} from './api';
import toast from '../../components/Toast';
import { AppShell } from '../../platform/AppShell';
import { getApplicationIcon } from '../applications';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';
import { formatBeijingDate, formatBeijingDateKey } from '../../utils/time';

// 复刻强度选项
const REPLICATE_STRENGTH_OPTIONS = [
  { value: 'low', label: '低', description: '仅参考风格' },
  { value: 'medium', label: '中', description: '参考风格、构图、光影' },
  { value: 'high', label: '高', description: '尽量贴近参考图' },
] as const;

// 图片尺寸选项（电商常用尺寸）
const SIZE_OPTIONS = [
  { value: 'auto', label: '自动', scene: '跟随素材参考图' },
  { value: '800x800', label: '800×800', scene: '电商主图' },
  { value: '1000x1000', label: '1000×1000', scene: '天猫高清主图' },
  { value: '1000x1334', label: '1000×1334', scene: '详情页竖版' },
  { value: '1334x1000', label: '1334x1000', scene: '详情页横版' },
  { value: '800x1200', label: '800×1200', scene: '手机端详情' },
  { value: '1200x800', label: '1200×800', scene: 'PC端Banner' },
  { value: '1920x600', label: '1920×600', scene: '全宽Banner' },
  { value: '750x350', label: '750×350', scene: '移动端Banner' },
  { value: '1000x2000', label: '1000×2000', scene: '长图详情' },
  { value: '2000x1000', label: '2000×1000', scene: '横版海报' },
] as const;

// 清晰度选项
const QUALITY_OPTIONS = [
  { value: 'low', label: '标准', description: '速度快' },
  { value: 'medium', label: '高清', description: '质量好' },
  { value: 'high', label: '超清', description: '最佳效果' },
] as const;

// 模型配置类型
interface ModelConfig {
  id: string;
  name: string;
  description?: string;
  enableText2img: boolean;
  enableImg2img: boolean;
}

// 图片上传组件
function ImageUploader({
  value,
  onChange,
  onPreview,
  placeholder = '点击上传图片',
  showHint = true,
  className = '',
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  onPreview?: (image: string) => void;
  placeholder?: string;
  showHint?: boolean;
  className?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('图片大小不能超过 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      onChange(base64);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      {value ? (
        <div className="group relative h-full overflow-hidden rounded-3xl bg-slate-100 shadow-inner">
          <img
            src={resolveBrowserMediaUrl(value)}
            alt="Uploaded"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/45 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
          {onPreview && (
            <button
              onClick={() => onPreview(value)}
              className="absolute left-3 top-3 rounded-full bg-white/95 p-2 text-slate-700 opacity-0 shadow-lg transition hover:bg-sky-50 hover:text-sky-600 group-hover:opacity-100"
              title="放大预览"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onChange(null)}
            className="absolute right-3 top-3 rounded-full bg-white/95 p-2 text-rose-600 opacity-0 shadow-lg transition hover:bg-rose-50 group-hover:opacity-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex h-full min-h-[140px] w-full flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-slate-300 bg-white/70 px-5 text-center transition hover:border-sky-300 hover:bg-sky-50/70 hover:shadow-inner"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-300/60">
            <Upload className="h-5 w-5" />
          </span>
          <span className="text-sm font-medium text-slate-700">{placeholder}</span>
          {showHint && <span className="text-xs text-slate-400">支持 JPG / PNG，最大 10MB</span>}
        </button>
      )}
    </div>
  );
}

function ImagePreviewModal({
  image,
  onClose,
}: {
  image: string | null;
  onClose: () => void;
}) {
  if (!image) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="relative max-h-[92vh] max-w-[92vw]" onClick={(e) => e.stopPropagation()}>
        <img
          src={resolveBrowserMediaUrl(image)}
          alt="图片预览"
          className="max-h-[92vh] max-w-[92vw] rounded-3xl object-contain shadow-2xl shadow-black/40"
        />
        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-full bg-white/95 p-2.5 text-slate-700 shadow-lg transition hover:bg-rose-50 hover:text-rose-600"
          title="关闭预览"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

// 素材节点卡片组件 - 直接编辑模式
function NodeCard({
  node,
  onUpdate,
  onDelete,
  onGenerate,
  models,
  creditPreview,
  onPreviewImage,
}: {
  node: EcommerceReplicateNode;
  onUpdate: (id: string, data: Partial<EcommerceReplicateNode>) => void;
  onDelete: (id: string) => void;
  onGenerate: (id: string) => void;
  models: ModelConfig[];
  creditPreview?: EcommerceCreditsPreview;
  onPreviewImage: (image: string) => void;
}) {
  // 本地编辑状态
  const [editTitle, setEditTitle] = useState(node.title);
  const [editPrompt, setEditPrompt] = useState(node.prompt || '');
  const [editReferenceImage, setEditReferenceImage] = useState(node.referenceImage);
  const [editStrength, setEditStrength] = useState(node.replicateStrength);
  const [editModel, setEditModel] = useState(node.model || (models[0]?.id ?? ''));
  const [editSize, setEditSize] = useState(node.size);
  const [editCustomSize, setEditCustomSize] = useState(
    SIZE_OPTIONS.some((option) => option.value === node.size) ? '' : node.size
  );
  const [editQuality, setEditQuality] = useState(node.quality);

  // 当节点数据变化时同步本地状态
  useEffect(() => {
    setEditTitle(node.title);
    setEditPrompt(node.prompt || '');
    setEditReferenceImage(node.referenceImage);
    setEditStrength(node.replicateStrength);
    setEditModel(node.model || (models[0]?.id ?? ''));
    setEditSize(node.size);
    setEditCustomSize(SIZE_OPTIONS.some((option) => option.value === node.size) ? '' : node.size);
    setEditQuality(node.quality);
  }, [node, models]);

  // 自动保存（防抖）
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedSnapshotRef = useRef('');
  const handleAutoSave = useCallback(() => {
    const nextData = {
      title: editTitle,
      prompt: editPrompt,
      referenceImage: editReferenceImage,
      replicateStrength: editStrength,
      model: editModel,
      size: editCustomSize.trim() || editSize,
      quality: editQuality,
    };
    const nextSnapshot = JSON.stringify(nextData);

    if (nextSnapshot === lastSavedSnapshotRef.current) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      lastSavedSnapshotRef.current = nextSnapshot;
      onUpdate(node.id, nextData);
    }, 800);
  }, [node.id, editTitle, editPrompt, editReferenceImage, editStrength, editModel, editSize, editCustomSize, editQuality, onUpdate]);

  useEffect(() => {
    lastSavedSnapshotRef.current = JSON.stringify({
      title: node.title,
      prompt: node.prompt || '',
      referenceImage: node.referenceImage,
      replicateStrength: node.replicateStrength,
      model: node.model || (models[0]?.id ?? ''),
      size: node.size,
      quality: node.quality,
    });
  }, [node.id, node.title, node.prompt, node.referenceImage, node.replicateStrength, node.model, node.size, node.quality, models]);

  // 当任何编辑值变化时触发自动保存
  useEffect(() => {
    handleAutoSave();
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [handleAutoSave]);

  // 获取模型显示名称
  const getModelDisplayName = useCallback(
    (modelId: string | null) => {
      if (!modelId) return '未设置';
      const found = models.find((m) => m.id === modelId);
      return found?.name || modelId;
    },
    [models]
  );

  const getStatusIcon = () => {
    switch (node.status) {
      case 'generating':
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case 'success':
        return <CheckCircle2 className="h-4 w-4" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <div className="h-2.5 w-2.5 rounded-full bg-white/70" />;
    }
  };

  const getStatusText = () => {
    switch (node.status) {
      case 'generating':
        return '生成中';
      case 'success':
        return '已完成';
      case 'failed':
        return '生成失败';
      default:
        return '待生成';
    }
  };

  return (
    <div className="overflow-hidden rounded-[28px] border border-white/70 bg-white/90 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-xl">
      {/* 头部 */}
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg shadow-sky-200/70">
            {getStatusIcon()}
          </div>
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[15px] font-semibold text-slate-900 outline-none placeholder:text-slate-400"
            placeholder="节点名称"
          />
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-medium ${
              node.status === 'success'
                ? 'bg-emerald-50 text-emerald-700'
                : node.status === 'failed'
                  ? 'bg-rose-50 text-rose-700'
                  : node.status === 'generating'
                    ? 'bg-sky-50 text-sky-700'
                    : 'bg-slate-100 text-slate-600'
            }`}
          >
            {getStatusText()}
          </span>
          <button
            onClick={() => onDelete(node.id)}
            className="rounded-xl p-2 text-slate-400 transition hover:bg-rose-50 hover:text-rose-600"
            title="删除节点"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="p-3 sm:p-4">
        {/* 参考图 */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">
              <ImageIcon className="h-3 w-3" />
              参考图
            </label>
            <span className="text-[10px] text-slate-400">素材来源</span>
          </div>
          <ImageUploader
            value={editReferenceImage}
            onChange={setEditReferenceImage}
            onPreview={onPreviewImage}
            placeholder="上传参考图"
            className="h-[150px] sm:h-[160px]"
          />
        </div>

        {/* 文字描述 + 参数（全部放在参考图下方） */}
        <div className="mt-3 space-y-2">
          {/* 文字描述 */}
          <div className="space-y-1">
            <label className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">文字描述</label>
            <textarea
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              placeholder="补充要求，如：背景更通透..."
              className="h-[56px] w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
            />
          </div>

          {/* 模型 */}
          <div className="space-y-1">
            <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">
              <Cpu className="h-3 w-3" />模型
            </label>
            {models.length === 0 ? (
              <div className="w-full rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs font-medium text-slate-400">
                {getModelDisplayName(editModel)}
              </div>
            ) : (
              <select
                value={editModel}
                onChange={(e) => setEditModel(e.target.value)}
                className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-900 outline-none transition focus:border-sky-400"
              >
                {models.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* 尺寸 */}
          <div className="space-y-1">
            <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">
              <Maximize2 className="h-3 w-3" />尺寸
            </label>
            <div className="grid gap-2 sm:grid-cols-[1fr_96px]">
              <select
                value={SIZE_OPTIONS.some((option) => option.value === editSize) ? editSize : 'custom'}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    setEditSize('custom');
                    setEditCustomSize(editCustomSize || node.size || '800x800');
                  } else {
                    setEditSize(value);
                    setEditCustomSize('');
                  }
                }}
                className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-900 outline-none transition focus:border-sky-400"
              >
                {SIZE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label} - {o.scene}</option>
                ))}
                <option value="custom">自定义尺寸</option>
              </select>
              <input
                value={editCustomSize}
                onChange={(e) => {
                  setEditCustomSize(e.target.value);
                  if (e.target.value.trim()) setEditSize('custom');
                }}
                placeholder="如 900x1200"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white"
              />
            </div>
          </div>

          {/* 清晰度 + 复刻强度 并排 */}
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">
                <Gauge className="h-3 w-3" />清晰度
              </label>
              <select
                value={editQuality}
                onChange={(e) => setEditQuality(e.target.value as 'low' | 'medium' | 'high')}
                className="h-8 w-full appearance-none rounded-xl border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-900 outline-none transition focus:border-sky-400"
              >
                {QUALITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* 复刻强度 */}
            <div className="space-y-1">
              <label className="flex h-3 items-center gap-1 text-[10px] font-medium uppercase tracking-[0.1em] text-slate-500">
                <SlidersHorizontal className="h-3 w-3" />复刻强度
              </label>
              <div className="flex h-8 gap-1">
                {REPLICATE_STRENGTH_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setEditStrength(option.value)}
                    className={`flex-1 rounded-xl border text-center transition ${
                      editStrength === option.value
                        ? 'border-sky-400 bg-sky-50 text-sky-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-sky-200'
                    }`}
                  >
                    <span className="text-[11px] font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* 生成结果 */}
        <div className="mt-3 rounded-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-3 text-white">
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <label className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-300">
              <Sparkles className="mr-1 inline h-3 w-3 text-sky-300" />
              生成结果
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${creditPreview?.enough === false ? 'bg-rose-400/15 text-rose-200' : 'bg-amber-300/15 text-amber-100'}`}>
                <Coins className="h-3 w-3" />
                {creditPreview ? `${creditPreview.credits} 积分` : '积分计算中'}
              </span>
              <button
                onClick={() => onGenerate(node.id)}
                disabled={node.status === 'generating'}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-900 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {node.status === 'generating' ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    生成中
                  </>
                ) : node.outputImage ? (
                  <>
                    <RefreshCw className="h-3 w-3" />
                    重新生成
                  </>
                ) : (
                  <>
                    <Wand2 className="h-3 w-3" />
                    生成
                  </>
                )}
              </button>
            </div>
          </div>

          {node.outputImage ? (
            <div className="group relative overflow-hidden rounded-xl bg-white/5">
              <img
                src={resolveBrowserMediaUrl(node.outputImage)}
                alt="Result"
                className="h-48 w-full object-contain transition duration-300 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/55 via-slate-950/10 to-transparent opacity-0 transition group-hover:opacity-100" />
              <button
                onClick={() => onPreviewImage(node.outputImage!)}
                className="absolute left-3 top-3 inline-flex items-center justify-center rounded-full bg-white/95 p-2 text-slate-700 shadow-lg transition hover:bg-sky-50 hover:text-sky-600 sm:opacity-0 sm:group-hover:opacity-100"
                title="放大预览"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
              <a
                href={resolveBrowserMediaUrl(node.outputImage, { download: true })}
                download
                className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-2 text-xs font-medium text-slate-800 shadow-lg transition hover:bg-sky-50 hover:text-sky-600 sm:opacity-0 sm:group-hover:opacity-100"
              >
                <Download className="h-3.5 w-3.5" />
                下载
              </a>
            </div>
          ) : (
            <div className="flex h-36 flex-col items-center justify-center rounded-xl border border-dashed border-white/15 bg-white/5 text-center">
              <Sparkles className="mb-2 h-6 w-6 text-sky-300" />
              <p className="text-xs text-slate-300">点击生成</p>
            </div>
          )}
        </div>

        {/* 错误信息 */}
        {node.errorMessage && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {node.errorMessage}
          </div>
        )}
      </div>
    </div>
  );
}

// 任务列表侧边栏
function TaskSidebar({
  tasks,
  currentTaskId,
  onSelectTask,
  onCreateTask,
  onDeleteTask,
  className = '',
}: {
  tasks: EcommerceReplicateTask[];
  currentTaskId: string | null;
  onSelectTask: (id: string) => void;
  onCreateTask: () => void;
  onDeleteTask: (id: string) => void;
  className?: string;
}) {
  return (
    <aside className={`h-full overflow-y-auto border-white/70 bg-white/75 shadow-[12px_0_40px_rgba(15,23,42,0.04)] backdrop-blur-xl lg:sticky lg:top-16 lg:h-[calc(100vh-64px)] lg:w-80 lg:shrink-0 lg:border-r ${className}`}>
      <div className="p-5">
        <div className="mb-5 rounded-[28px] bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white shadow-2xl shadow-slate-300/40">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/10">
              <LayoutGrid className="h-4 w-4 text-sky-200" />
            </span>
            <h2 className="text-lg font-semibold leading-none">任务库</h2>
          </div>
          <button
            onClick={onCreateTask}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:scale-[1.01] hover:bg-sky-50"
          >
            <Plus className="h-4 w-4" />
            新建复刻任务
          </button>
        </div>

        <div className="space-y-2">
          {tasks.map((task) => {
            const percent = task.nodeCount > 0 ? Math.round((task.completedCount / task.nodeCount) * 100) : 0;

            return (
              <div
                key={task.id}
                onClick={() => onSelectTask(task.id)}
                className={`group cursor-pointer rounded-3xl border p-3 transition ${
                  currentTaskId === task.id
                    ? 'border-sky-200 bg-sky-50/90 shadow-lg shadow-sky-100/70'
                    : 'border-white/80 bg-white/80 hover:border-slate-200 hover:bg-white hover:shadow-md'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-100">
                    {task.productImage ? (
                      <img
                        src={resolveBrowserMediaUrl(task.productImage)}
                        alt="Product"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <ImageIcon className="h-5 w-5 text-slate-400" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {task.title}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                      <span>{task.completedCount}/{task.nodeCount} 完成</span>
                      <span>·</span>
                      <span>{formatBeijingDate(task.updatedAt)}</span>
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 transition-all"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteTask(task.id);
                    }}
                    className="rounded-xl p-2 text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}

          {tasks.length === 0 && (
            <div className="rounded-[28px] border border-dashed border-slate-200 bg-white/70 p-8 text-center">
              <LayoutGrid className="mx-auto mb-3 h-12 w-12 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">暂无任务</p>
              <p className="mt-1 text-xs text-slate-400">创建一个任务开始复刻</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

// 主页面组件
export default function EcommerceReplicatePage() {
  const navigate = useNavigate();
  const { taskId: urlTaskId } = useParams<{ taskId?: string }>();

  const [tasks, setTasks] = useState<EcommerceReplicateTask[]>([]);
  const [currentTask, setCurrentTask] = useState<EcommerceReplicateTaskDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [nodeCreditPreviews, setNodeCreditPreviews] = useState<Record<string, EcommerceCreditsPreview>>({});
  const [taskCreditPreview, setTaskCreditPreview] = useState<EcommerceCreditsPreview | null>(null);
  const [productDescription, setProductDescription] = useState('');
  const [editingTaskTitle, setEditingTaskTitle] = useState(false);
  const [taskTitleInput, setTaskTitleInput] = useState('');
  const [defaultModelId, setDefaultModelId] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);

  // 节点轮询控制：句柄按 nodeId 存储便于清理/去重；currentTaskIdRef 用于任务切换后停止旧轮询
  const pollTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const currentTaskIdRef = useRef<string | null>(null);

  // 卸载时停止全部轮询，避免对已卸载组件继续请求和写入状态
  useEffect(() => {
    return () => {
      pollTimersRef.current.forEach((timer) => clearTimeout(timer));
      pollTimersRef.current.clear();
    };
  }, []);

  // 加载模型列表
  const loadModels = useCallback(async () => {
    try {
      const [defaultRes, modelsRes] = await Promise.all([api.getDefaultModel(), api.getModels()]);
      const img2imgModels = modelsRes.data.filter((m: ModelConfig) => m.enableImg2img);
      setModels(img2imgModels);
      setDefaultModelId(defaultRes.defaultModelId && img2imgModels.some((m) => m.id === defaultRes.defaultModelId) ? defaultRes.defaultModelId : null);
    } catch (error) {
      console.error('加载模型失败:', error);
    }
  }, []);

  // 加载任务列表
  const loadTasks = useCallback(async () => {
    try {
      const res = await ecommerceReplicateAPI.getTasks();
      setTasks(res.data);
      return res.data;
    } catch (error) {
      toast.error('获取任务列表失败');
      return [];
    }
  }, []);

  // 加载任务详情
  const loadTaskDetail = useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      currentTaskIdRef.current = id;
      const res = await ecommerceReplicateAPI.getTask(id);
      // await 期间用户可能已切换任务，避免旧响应覆盖新任务
      if (currentTaskIdRef.current !== id) return;
      setCurrentTask(res.data);
      setProductDescription(res.data.productDescription || '');
    } catch (error) {
      toast.error('获取任务详情失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初始化
  useEffect(() => {
    loadModels();
    loadTasks().then((loadedTasks) => {
      if (urlTaskId) {
        loadTaskDetail(urlTaskId);
      } else if (loadedTasks.length > 0) {
        navigate(`/apps/ecommerce-replicate/${loadedTasks[0].id}`, { replace: true });
      }
    });
  }, [urlTaskId, loadTasks, loadTaskDetail, navigate, loadModels]);

  const refreshCreditsPreview = useCallback(async (task: EcommerceReplicateTaskDetail | null = currentTask) => {
    if (!task) return;

    try {
      const [taskPreview, nodePreviewResults] = await Promise.all([
        ecommerceReplicateAPI.getTaskCreditsPreview(task.id),
        Promise.all(
          task.nodes.map(async (node) => {
            try {
              const preview = await ecommerceReplicateAPI.getNodeCreditsPreview(node.id);
              return [node.id, preview.data] as const;
            } catch {
              return null;
            }
          })
        ),
      ]);
      setTaskCreditPreview(taskPreview.data);
      setNodeCreditPreviews(Object.fromEntries(nodePreviewResults.filter(Boolean) as Array<readonly [string, EcommerceCreditsPreview]>));
    } catch (error) {
      console.error('获取积分预览失败:', error);
    }
  }, [currentTask]);

  useEffect(() => {
    refreshCreditsPreview(currentTask);
  }, [currentTask, refreshCreditsPreview]);

  const lastSavedProductDescriptionRef = useRef('');

  useEffect(() => {
    lastSavedProductDescriptionRef.current = currentTask?.productDescription || '';
    setProductDescription(currentTask?.productDescription || '');
  }, [currentTask?.id]);

  useEffect(() => {
    if (!currentTask) return;
    if (productDescription.trim() === lastSavedProductDescriptionRef.current) return;

    const timer = setTimeout(async () => {
      try {
        const nextDescription = productDescription.trim();
        const res = await ecommerceReplicateAPI.updateTask(currentTask.id, {
          productDescription: nextDescription || null,
        });
        lastSavedProductDescriptionRef.current = res.data.productDescription || '';
        setCurrentTask(res.data);
      } catch (error) {
        toast.error('更新产品描述失败');
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [currentTask?.id, productDescription]);

  // 创建新任务
  const handleCreateTask = async () => {
    try {
      const title = `复刻任务 ${tasks.length + 1}`;
      const res = await ecommerceReplicateAPI.createTask({ title });
      setTasks((prev) => [res.data, ...prev]);
      navigate(`/apps/ecommerce-replicate/${res.data.id}`);
      toast.success('任务创建成功');
    } catch (error) {
      toast.error('创建任务失败');
    }
  };

  // 删除任务
  const handleDeleteTask = async (id: string) => {
    if (!confirm('确定要删除这个任务吗？')) return;

    try {
      await ecommerceReplicateAPI.deleteTask(id);
      setTasks((prev) => prev.filter((t) => t.id !== id));
      if (currentTask?.id === id) {
        const remaining = tasks.filter((t) => t.id !== id);
        if (remaining.length > 0) {
          navigate(`/apps/ecommerce-replicate/${remaining[0].id}`);
        } else {
          navigate('/apps/ecommerce-replicate');
          setCurrentTask(null);
        }
      }
      toast.success('任务已删除');
    } catch (error) {
      toast.error('删除任务失败');
    }
  };

  // 更新任务产品图
  const handleUpdateProductImage = async (key: 'productImage' | 'productImage2' | 'productImage3', image: string | null) => {
    if (!currentTask) return;

    try {
      const res = await ecommerceReplicateAPI.updateTask(currentTask.id, {
        [key]: image,
      });
      setCurrentTask(res.data);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === currentTask.id ? { ...t, [key]: image } : t
        )
      );
    } catch (error) {
      toast.error('更新产品图失败');
    }
  };

  // 添加节点
  const handleAddNode = async () => {
    if (!currentTask) return;

    try {
      const defaultModel = defaultModelId || models[0]?.id || 'gpt-image-2';
      const res = await ecommerceReplicateAPI.createNode(currentTask.id, {
        title: `素材节点 ${currentTask.nodes.length + 1}`,
        model: defaultModel,
        size: 'auto',
        quality: 'low',
      });
      setCurrentTask((prev) =>
        prev
          ? {
              ...prev,
              nodes: [...prev.nodes, res.data],
            }
          : null
      );
      toast.success('节点添加成功');
    } catch (error) {
      toast.error('添加节点失败');
    }
  };

  // 更新节点
  const handleUpdateNode = useCallback(async (
    id: string,
    data: Partial<EcommerceReplicateNode>
  ) => {
    if (!currentTask) return;

    try {
      const res = await ecommerceReplicateAPI.updateNode(id, data);
      setCurrentTask((prev) =>
        prev
          ? {
              ...prev,
              nodes: prev.nodes.map((n) => (n.id === id ? res.data : n)),
            }
          : null
      );
    } catch (error) {
      toast.error('更新节点失败');
    }
  }, [currentTask]);

  // 删除节点
  const handleDeleteNode = async (id: string) => {
    if (!confirm('确定要删除这个节点吗？')) return;

    try {
      await ecommerceReplicateAPI.deleteNode(id);
      setCurrentTask((prev) =>
        prev
          ? {
              ...prev,
              nodes: prev.nodes.filter((n) => n.id !== id),
            }
          : null
      );
      toast.success('节点已删除');
    } catch (error) {
      toast.error('删除节点失败');
    }
  };

  // 生成单个节点
  const handleGenerateNode = async (id: string) => {
    try {
      await ecommerceReplicateAPI.generateNode(id);
      // 更新节点状态
      setCurrentTask((prev) =>
        prev
          ? {
              ...prev,
              nodes: prev.nodes.map((n) =>
                n.id === id ? { ...n, status: 'generating' as const } : n
              ),
            }
          : null
      );
      toast.success('生成任务已提交');

      // 轮询状态（显式传入 taskId，消除闭包读旧任务的竞态）
      if (currentTask) {
        pollNodeStatus(currentTask.id, id);
      }
    } catch (error: any) {
      toast.error(error.message || '生成失败');
    }
  };

  /**
   * 轮询节点状态（3 秒 / 最多 60 次节奏不变）。
   * - taskId 显式传入，不读闭包里的 currentTask；
   * - 任务切换（currentTaskIdRef 不一致）或组件卸载后停止；
   * - 同一节点重复发起时先清理旧轮询链，避免叠加竞态；
   * - 轮询期间的瞬时错误继续重试，避免一次网络抖动让节点永远停在 generating。
   */
  const pollNodeStatus = (taskId: string, nodeId: string) => {
    const maxAttempts = 60;
    let attempts = 0;

    const isPollActive = () =>
      currentTaskIdRef.current === taskId && pollTimersRef.current.has(nodeId);

    const schedule = () => {
      const timer = setTimeout(check, 3000);
      pollTimersRef.current.set(nodeId, timer);
    };

    const check = async () => {
      if (attempts >= maxAttempts) {
        pollTimersRef.current.delete(nodeId);
        return;
      }
      attempts++;

      try {
        const res = await ecommerceReplicateAPI.getTask(taskId);
        // await 期间任务可能已切换或轮询已被清理，二次校验后再写状态
        if (!isPollActive()) return;

        const node = res.data.nodes.find((n) => n.id === nodeId);
        if (node && (node.status === 'success' || node.status === 'failed')) {
          setCurrentTask((prev) =>
            prev?.id === taskId
              ? {
                  ...prev,
                  nodes: prev.nodes.map((n) =>
                    n.id === nodeId ? node : n
                  ),
                }
              : prev
          );
          // 更新任务列表中的完成数
          setTasks((prev) =>
            prev.map((t) =>
              t.id === taskId
                ? {
                    ...t,
                    completedCount: res.data.nodes.filter(
                      (n) => n.status === 'success'
                    ).length,
                  }
                : t
            )
          );
          pollTimersRef.current.delete(nodeId);
        } else if (node?.status === 'generating') {
          schedule();
        } else {
          pollTimersRef.current.delete(nodeId);
        }
      } catch (error) {
        console.error('轮询节点状态失败:', error);
        // 瞬时错误继续重试（仍受 60 次上限约束）
        if (isPollActive()) {
          schedule();
        }
      }
    };

    // 去重：同一节点已有轮询链时先停止旧的
    const existing = pollTimersRef.current.get(nodeId);
    if (existing) {
      clearTimeout(existing);
    }
    schedule();
  };

  // 一键生成全部
  const handleGenerateAll = async () => {
    if (!currentTask) return;

    const idleNodes = currentTask.nodes.filter(
      (n) => n.status === 'idle' || n.status === 'failed'
    );

    if (idleNodes.length === 0) {
      toast('没有待生成的节点');
      return;
    }

    try {
      setIsGeneratingAll(true);
      const res = await ecommerceReplicateAPI.generateAll(currentTask.id);

      // 更新节点状态
      setCurrentTask((prev) =>
        prev
          ? {
              ...prev,
              nodes: prev.nodes.map((n) =>
                res.results.find((r) => r.nodeId === n.id)
                  ? { ...n, status: 'generating' as const }
                  : n
              ),
            }
          : null
      );

      toast.success(`已提交 ${res.nodeCount} 个生成任务`);

      // 轮询所有节点状态
      res.results.forEach((r) => pollNodeStatus(currentTask.id, r.nodeId));
    } catch (error: any) {
      toast.error(error.message || '批量生成失败');
    } finally {
      setIsGeneratingAll(false);
    }
  };

  // 批量下载所有生成结果
  const handleBatchDownload = async () => {
    if (!currentTask) return;

    const successNodes = currentTask.nodes.filter((n) => n.status === 'success' && n.outputImage);
    if (successNodes.length === 0) {
      toast('没有可下载的生成结果');
      return;
    }

    try {
      // 使用 JSZip 打包下载
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      const folder = zip.folder(currentTask.title || '电商复刻结果');

      let downloadedCount = 0;
      for (const node of successNodes) {
        if (!node.outputImage) continue;
        try {
          // 循环内逐次解析：mediaToken 30 分钟过期时惰性刷新可生效
          const response = await fetch(resolveBrowserMediaUrl(node.outputImage));
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const blob = await response.blob();
          const ext = blob.type.includes('png') ? 'png' : 'jpg';
          const fileName = `${node.title || '节点'}_${node.id.slice(-6)}.${ext}`;
          folder?.file(fileName, blob);
          downloadedCount++;
        } catch (err) {
          console.error(`下载节点 ${node.id} 图片失败:`, err);
        }
      }

      if (downloadedCount === 0) {
        toast('下载失败，请重试');
        return;
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentTask.title || '电商复刻结果'}_${formatBeijingDateKey()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`已下载 ${downloadedCount} 张图片`);
    } catch (error) {
      console.error('批量下载失败:', error);
      toast.error('批量下载失败，请重试');
    }
  };

  return (
    <AppShell
      appName="电商复刻工作台"
      appSubtitle="产品图 × 目标素材 → 批量生成电商图"
      appIcon={(() => { const Icon = getApplicationIcon('ecommerceReplicate'); return <Icon className="h-5 w-5" />; })()}
      backgroundClassName="bg-[#eef3f8]"
      onOpenSidebar={() => setIsTaskDrawerOpen(true)}
      headerRight={
        currentTask && (
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <div className="hidden rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-600 md:block">
              {currentTask.nodes.filter((n) => n.status === 'success').length}/{currentTask.nodes.length} 已完成
            </div>
            <div className={`hidden items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold md:inline-flex ${taskCreditPreview?.enough === false ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-700'}`}>
              <Coins className="h-4 w-4" />
              全部预计 {taskCreditPreview ? taskCreditPreview.credits : '-'} 积分
            </div>
            <button
              onClick={handleBatchDownload}
              disabled={!currentTask.nodes.some((n) => n.status === 'success' && n.outputImage)}
              className="hidden items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-lg shadow-slate-200/70 transition hover:border-sky-200 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50 sm:flex"
            >
              <Download className="h-4 w-4" />
              <span className="hidden lg:inline">批量</span>下载
            </button>
            <button
              onClick={handleGenerateAll}
              disabled={isGeneratingAll}
              className="flex items-center gap-1.5 rounded-2xl bg-slate-950 px-3 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-2 sm:px-5"
            >
              {isGeneratingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">一键生成全部</span>
              <span className="sm:hidden">生成</span>
            </button>
          </div>
        )
      }
    >
      <ImagePreviewModal image={previewImage} onClose={() => setPreviewImage(null)} />
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-28 left-[18%] h-80 w-80 rounded-full bg-sky-200/50 blur-3xl" />
        <div className="absolute right-[-8%] top-40 h-96 w-96 rounded-full bg-cyan-200/40 blur-3xl" />
        <div className="absolute bottom-[-12%] left-[42%] h-96 w-96 rounded-full bg-amber-100/60 blur-3xl" />
      </div>

      <div className="relative lg:flex">
        <TaskSidebar
          tasks={tasks}
          currentTaskId={currentTask?.id || null}
          onSelectTask={(id) => navigate(`/apps/ecommerce-replicate/${id}`)}
          onCreateTask={handleCreateTask}
          onDeleteTask={handleDeleteTask}
          className="hidden lg:block"
        />

        {isTaskDrawerOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden">
            <button
              aria-label="关闭任务库"
              className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
              onClick={() => setIsTaskDrawerOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-[86vw] max-w-[360px] overflow-hidden border-r border-white/70 bg-white shadow-2xl shadow-slate-950/25">
              <TaskSidebar
                tasks={tasks}
                currentTaskId={currentTask?.id || null}
                onSelectTask={(id) => {
                  navigate(`/apps/ecommerce-replicate/${id}`);
                  setIsTaskDrawerOpen(false);
                }}
                onCreateTask={() => {
                  handleCreateTask();
                  setIsTaskDrawerOpen(false);
                }}
                onDeleteTask={handleDeleteTask}
              />
            </div>
          </div>
        )}

        <main className="min-h-[calc(100vh-64px)] flex-1 overflow-y-auto lg:h-[calc(100vh-64px)]">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="rounded-3xl bg-white/80 p-6 shadow-xl shadow-slate-200/80">
                <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
              </div>
            </div>
          ) : currentTask ? (
            <div className="mx-auto max-w-[1380px] px-3 py-4 sm:px-6 sm:py-8">
              <section className="mb-5 overflow-hidden rounded-[28px] border border-white/70 bg-white/85 shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl sm:mb-6 sm:rounded-[36px]">
                <div className="grid gap-0 xl:grid-cols-[360px_1fr]">
                  <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white sm:p-7">
                    <div className="absolute right-[-80px] top-[-80px] h-52 w-52 rounded-full bg-sky-400/20 blur-2xl" />
                    <div className="relative">
                      <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-slate-200">
                        <Sparkles className="h-3.5 w-3.5 text-sky-200" />
                        当前任务
                      </div>
                      {editingTaskTitle ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={taskTitleInput}
                            onChange={(e) => setTaskTitleInput(e.target.value)}
                            onBlur={async () => {
                              if (taskTitleInput.trim() && taskTitleInput !== currentTask.title) {
                                try {
                                  const res = await ecommerceReplicateAPI.updateTask(currentTask.id, { title: taskTitleInput.trim() });
                                  setCurrentTask(res.data);
                                  setTasks((prev) => prev.map((t) => (t.id === currentTask.id ? { ...t, title: taskTitleInput.trim() } : t)));
                                  toast.success('任务名称已更新');
                                } catch {
                                  toast.error('更新失败');
                                }
                              }
                              setEditingTaskTitle(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                (e.target as HTMLInputElement).blur();
                              }
                              if (e.key === 'Escape') {
                                setEditingTaskTitle(false);
                              }
                            }}
                            autoFocus
                            className="flex-1 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-2xl font-semibold text-white outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white/20"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <h1 className="break-words text-2xl font-semibold tracking-tight sm:text-3xl">{currentTask.title}</h1>
                          <button
                            onClick={() => {
                              setTaskTitleInput(currentTask.title);
                              setEditingTaskTitle(true);
                            }}
                            className="rounded-lg p-1.5 text-slate-300 transition hover:bg-white/10 hover:text-white"
                            title="修改任务名称"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      <p className="mt-3 text-sm leading-6 text-slate-300">先上传你的产品图，再添加多个目标素材节点。每个节点会独立生成一个结果，适合批量复刻竞品场景。</p>
                      <div className="mt-7 grid grid-cols-3 gap-2 sm:gap-3">
                        <div className="rounded-2xl bg-white/10 p-3">
                          <div className="text-2xl font-semibold">{currentTask.nodes.length}</div>
                          <div className="mt-1 text-xs text-slate-300">素材节点</div>
                        </div>
                        <div className="rounded-2xl bg-white/10 p-3">
                          <div className="text-2xl font-semibold">{currentTask.nodes.filter((n) => n.status === 'success').length}</div>
                          <div className="mt-1 text-xs text-slate-300">已完成</div>
                        </div>
                        <div className="rounded-2xl bg-white/10 p-3">
                          <div className="text-2xl font-semibold">1</div>
                          <div className="mt-1 text-xs text-slate-300">默认张数</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-5 p-4 sm:gap-6 sm:p-7 lg:grid-cols-[280px_1fr]">
                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                          <ImageIcon className="h-4 w-4 text-sky-500" />
                          我的产品
                        </h2>
                        <span className={`rounded-full px-3 py-1 text-xs font-medium ${currentTask.productImage ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                          {currentTask.productImage ? '主图已上传' : '主图待上传'}
                        </span>
                      </div>
                      <ImageUploader
                        value={currentTask.productImage}
                        onChange={(image) => handleUpdateProductImage('productImage', image)}
                        onPreview={setPreviewImage}
                        placeholder="上传产品主图"
                        className="h-56 sm:h-72"
                      />
                      <div className="mt-3 grid grid-cols-2 gap-3">
                        <ImageUploader
                          value={currentTask.productImage2}
                          onChange={(image) => handleUpdateProductImage('productImage2', image)}
                          onPreview={setPreviewImage}
                          placeholder="副图 1"
                          showHint={false}
                          className="h-32"
                        />
                        <ImageUploader
                          value={currentTask.productImage3}
                          onChange={(image) => handleUpdateProductImage('productImage3', image)}
                          onPreview={setPreviewImage}
                          placeholder="副图 2"
                          showHint={false}
                          className="h-32"
                        />
                      </div>
                      <p className="mt-2 text-center text-[11px] leading-none text-slate-400">支持 JPG / PNG，最大 10MB</p>
                    </div>

                    <div className="flex flex-col justify-between rounded-[24px] border border-slate-100 bg-slate-50/80 p-4 sm:rounded-[28px] sm:p-5">
                      <div>
                        <div className="mb-4 flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-900 shadow-sm">
                            <LayoutGrid className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-950">复刻流程</h3>
                            <p className="text-sm text-slate-500">产品主体保持不变，参考节点决定场景、构图和风格。</p>
                          </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-3">
                          {['上传产品图', '添加目标素材', '逐节点生成预览'].map((label, index) => (
                            <div key={label} className="rounded-2xl bg-white p-4 shadow-sm">
                              <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-full bg-slate-950 text-sm font-semibold text-white">{index + 1}</div>
                              <div className="text-sm font-semibold text-slate-900">{label}</div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">产品文字描述</label>
                          <textarea
                            value={productDescription}
                            onChange={(e) => setProductDescription(e.target.value)}
                            placeholder="填写产品名称、类目、材质、卖点或其他附加信息，例如：儿童保温杯，不锈钢内胆，适合开学季主图"
                            className="h-20 w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-2 focus:ring-sky-100"
                          />
                          <p className="mt-2 text-xs text-slate-400">这段信息会作为产品上下文参与每个节点生成，帮助模型更准确识别产品名称、类目和卖点。</p>
                        </div>
                      </div>
                      {!currentTask.productImage && (
                        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                          请先上传产品图，否则素材节点无法生成结果。
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <div className="mb-5 flex flex-col justify-between gap-4 md:flex-row md:items-end">
                  <div className="min-w-0">
                    <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/75 px-3 py-1 text-xs font-medium text-slate-500 shadow-sm">
                      <LayoutGrid className="h-3.5 w-3.5 text-sky-500" />
                      节点画布
                    </div>
                    <h2 className="text-xl font-semibold text-slate-950 sm:text-2xl">目标素材节点</h2>
                    <p className="mt-1 text-sm text-slate-500">每个节点可以独立设置模型、尺寸、清晰度等参数，修改后自动保存。</p>
                    <p className="mt-2 text-xs text-slate-400">系统会按渠道能力自动适配内部生成尺寸，最终输出和下载仍按你选择的电商尺寸交付。</p>
                    <div className={`mt-3 flex flex-wrap items-center gap-2 rounded-2xl px-3 py-2 text-sm font-semibold sm:inline-flex sm:px-4 ${taskCreditPreview?.enough === false ? 'bg-rose-50 text-rose-600' : 'bg-white/80 text-amber-700'}`}>
                      <Coins className="h-4 w-4" />
                      一键生成预计消耗 {taskCreditPreview ? taskCreditPreview.credits : '-'} 积分
                      {taskCreditPreview && <span className="text-xs font-medium text-slate-400">可生成 {taskCreditPreview.nodeCount} 个节点</span>}
                    </div>
                  </div>
                  <button
                    onClick={handleAddNode}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/80 bg-white px-5 py-3 text-sm font-semibold text-slate-900 shadow-lg shadow-slate-200/70 transition hover:-translate-y-0.5 hover:border-sky-200 hover:text-sky-700 md:w-auto"
                  >
                    <Plus className="h-4 w-4" />
                    添加素材节点
                  </button>
                </div>

                {currentTask.nodes.length === 0 ? (
                  <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/70 p-8 text-center shadow-xl shadow-slate-200/50 sm:rounded-[36px] sm:p-14">
                    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-950 text-white">
                      <LayoutGrid className="h-7 w-7" />
                    </div>
                    <p className="text-lg font-semibold text-slate-950">还没有素材节点</p>
                    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
                      添加第一个节点后，可以上传竞品参考图、填写描述，并在节点内直接查看生成结果。
                    </p>
                    <button
                      onClick={handleAddNode}
                      className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      <Plus className="h-4 w-4" />
                      添加第一个节点
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {currentTask.nodes.map((node) => (
                      <NodeCard
                        key={node.id}
                        node={node}
                        onUpdate={handleUpdateNode}
                        onDelete={handleDeleteNode}
                        onGenerate={handleGenerateNode}
                        models={models}
                        creditPreview={nodeCreditPreviews[node.id]}
                        onPreviewImage={setPreviewImage}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center p-6">
              <div className="max-w-md rounded-[36px] border border-white/70 bg-white/80 p-10 text-center shadow-2xl shadow-slate-200/80 backdrop-blur-xl">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-950 text-white">
                  <LayoutGrid className="h-7 w-7" />
                </div>
                <p className="text-lg font-semibold text-slate-950">选择一个任务或创建新任务</p>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  电商复刻可以帮你以自己的产品为主体，快速复刻参考素材的场景和视觉风格。
                </p>
                <button
                  onClick={handleCreateTask}
                  className="mt-6 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  创建新任务
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </AppShell>
  );
}
